import React from "react";
import { useTranslation } from "react-i18next";
import './InputMessage.scss';
import {
  AttachIcon,
  MicroIcon,
  SendIcon,
  QuickCommandIcon,
  InstructionIcon,
  MicroIconActive,
  SuggestionIcon,
  LoadingIcon,
  CloseIcon,
  PauseIcon,
  WarningIcon,
} from "@assets/icon";
import QuickSelectionDropdown from "../QuickSelectionDropdown/QuickSelectionDropdown";
import {
  getNumberSuggestions,
  getQuickFunctionSuggestions,
  getInstructionSuggestions,
  parseKeywordSuggestionInput,
  type KeywordSuggestionMode,
} from "@utils/index";
import { ChatAgentType } from "@constants/chat-agents";
import type { ChatMessage } from "../../../../types/chat-agent";
import QuotedMessagePreview from "./QuotedMessagePreview";
import MoneySuggestions from "./MoneySuggestions";
import ComponentPicker from "./ComponentPicker";
import { getComponentChipLabel, type ChatComponentItem } from '../../../../types/chat-component';
import { MAX_IMAGES_COUNT, resizeImage, validateFile } from "@utils/fileUpload";
import { chatService } from "@services/chatService";
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { useComponentPicker } from '../../../../hooks/useComponentPicker';

export type ChatUploadedImage = {
  contentType: string;
  imageUrl: string;
  name: string;
  uploadStatus: string;
};

interface InputMessageProps {
  onSendMessage: (message: string, images?: ChatUploadedImage[], component?: ChatComponentItem | null) => void;
  setToastMessage: (toast: any) => void;
  handleMicrophoneClick: () => void;
  stopMicAndDiscard?: () => void;
  inputValue: string;
  listening: boolean;
  onChangeInput: (value: string, cursorPosition?: number) => void;
  placeholder?: string;
  hasCommands?: boolean;
  activeAgent?: ChatAgentType;
  onInstructionClick?: () => void;
  onQuickFunctionClick?: () => void;
  quickFunctions?: any[];
  quickFunctionSearchList?: any[];
  instructions?: any[];
  quotedMessage?: ChatMessage | null;
  onCancelQuote?: () => void;
  isChatInputDisabled?: boolean;
  isBotSpeaking?: boolean;
  isThinking?: boolean;
  isMicDisabled?: boolean;
  onSelectQuickFunction?: (item: any) => void;
  onSelectInstruction?: (item: any) => void;
  isQuickFunctionsLoading?: boolean;
  isInstructionsLoading?: boolean;
  commands?: any[];
  onSelectCommand?: (item: any) => void;
  isCommandsLoading?: boolean;
  onStopSpeaking?: () => void;
  isBotStreaming?: boolean;
  onStopStreaming?: () => void;
  permissions?: Record<string, boolean> | null;
  isOwner?: boolean;
}

const KeywordSuggestionLine: React.FC<{ mode: KeywordSuggestionMode; text: string; inputValue: string }> = ({ mode, text, inputValue }) => {
  const fullPrefix = mode === 'INSTRUCTION' ? 'Hướng dẫn' : 'Mở';
  const leadRe = mode === 'INSTRUCTION' ? /^\s*Hướng dẫn\s*/i : /^\s*Mở\s*/i;
  const rawBody = text.replace(leadRe, '').trim();
  const fullText = rawBody ? `${fullPrefix} ${rawBody}` : fullPrefix;

  const trimmedInput = inputValue.trim();
  const lowFull = fullText.toLowerCase();
  const lowInput = trimmedInput.toLowerCase();

  // Direct prefix match (e.g. "hướng dẫn sử" → "Hướng dẫn sử dụng")
  if (lowInput.length > 0 && lowFull.startsWith(lowInput)) {
    const boldPart = fullText.substring(0, trimmedInput.length);
    const restPart = fullText.substring(trimmedInput.length);
    return (
      <span className="input-message-keyword-suggestion-label">
        <strong>{boldPart}</strong>{restPart}
      </span>
    );
  }

  // "?" / ">" only open the list — do not bold the first letter of "Hướng dẫn" / "Mở"
  if (/^(\?|>)$/.test(trimmedInput)) {
    return (
      <span className="input-message-keyword-suggestion-label">
        {fullText}
      </span>
    );
  }

  // Shorthand (huo, mo, ...) → bold the same number of characters as typed
  const boldLen = Math.min(trimmedInput.length, fullText.length);
  const boldPart = fullText.substring(0, boldLen);
  const restPart = fullText.substring(boldLen);
  return (
    <span className="input-message-keyword-suggestion-label">
      <strong>{boldPart}</strong>{restPart}
    </span>
  );
};


const InputMessage: React.FC<InputMessageProps> = ({
  onSendMessage,
  setToastMessage,
  handleMicrophoneClick,
  stopMicAndDiscard,
  inputValue,
  listening,
  onChangeInput,
  placeholder,
  hasCommands,
  activeAgent,
  onInstructionClick,
  onQuickFunctionClick,
  quickFunctions = [],
  quickFunctionSearchList = [],
  instructions = [],
  quotedMessage,
  onCancelQuote,
  isChatInputDisabled = false,
  isBotSpeaking = false,
  isThinking = false,
  isMicDisabled = false,
  onSelectQuickFunction,
  onSelectInstruction,
  isQuickFunctionsLoading = false,
  isInstructionsLoading = false,
  commands = [],
  onSelectCommand,
  isCommandsLoading = false,
  onStopSpeaking,
  isBotStreaming = false,
  onStopStreaming,
  permissions,
  isOwner = false,
}) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = React.useState(false);
  const [hideSuggestions, setHideSuggestions] = React.useState(false);
  const [isComponentMenuOpen, setIsComponentMenuOpen] = React.useState(false);
  const [componentDetailOpen, setComponentDetailOpen] = React.useState(false);
  const [attachedComponent, setAttachedComponent] = React.useState<ChatComponentItem | null>(null);
  const componentMenuRef = React.useRef<HTMLDivElement>(null);
  type ImageEntry = {
    id: string;
    previewUrl: string;
    status: 'uploading' | 'uploaded' | 'error';
    file?: File;
    uploadedData?: ChatUploadedImage;
  };

  const [imageEntries, setImageEntries] = React.useState<ImageEntry[]>([]);
  const uploadLoading = imageEntries.some(e => e.status === 'uploading');

  const {
    allowedTypes,
    selectedType,
    selectType,
    searchInput,
    setSearchInput,
    commitSearch,
    activeCache,
    loadMore,
  } = useComponentPicker({
    open: isComponentMenuOpen,
    permissions,
    isOwner,
  });

  const canShowComponentPicker =
    (activeAgent === ChatAgentType.AI || activeAgent === ChatAgentType.ANALYTICS_QUESTION) &&
    allowedTypes.length > 0;

  const displayPlaceholder = listening ? (t('common.listeningPlaceholder') || "Đang nghe bạn nói...") : placeholder; 
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const imageEntriesRef = React.useRef<ImageEntry[]>([]);
  imageEntriesRef.current = imageEntries;

  React.useEffect(() => {
    return () => {
      imageEntriesRef.current.forEach((entry) => URL.revokeObjectURL(entry.previewUrl));
    };
  }, []);

  const clearImages = React.useCallback(() => {
    setImageEntries((prev) => {
      prev.forEach((entry) => URL.revokeObjectURL(entry.previewUrl));
      return [];
    });
  }, []);

  const closeComponentMenu = React.useCallback(() => {
    setIsComponentMenuOpen(false);
    setComponentDetailOpen(false);
    setSearchInput('');
  }, [setSearchInput]);

  const handleAttachClick = () => {
    if (attachedComponent) {
      Modal.confirm({
        title: 'Thay đổi đính kèm?',
        content: 'Bạn đang đính kèm thành phần. Thêm ảnh sẽ gỡ thành phần đã chọn. Tiếp tục?',
        okText: 'Tiếp tục',
        cancelText: 'Hủy',
        onOk: () => {
          setAttachedComponent(null);
          fileInputRef.current?.click();
        },
      });
      return;
    }
    fileInputRef.current?.click();
  };

  const applyAttachedComponent = (item: ChatComponentItem) => {
    const apply = () => {
      setAttachedComponent(item);
      clearImages();
      closeComponentMenu();
    };

    if (imageEntries.length > 0 || (attachedComponent && attachedComponent.id !== item.id)) {
      Modal.confirm({
        title: 'Thay đổi đính kèm?',
        content: imageEntries.length > 0
          ? 'Bạn đang đính kèm ảnh. Chọn thành phần sẽ gỡ ảnh đã chọn. Tiếp tục?'
          : 'Bạn muốn thay thành phần đang đính kèm?',
        okText: 'Tiếp tục',
        cancelText: 'Hủy',
        onOk: apply,
      });
      return;
    }

    apply();
  };

  React.useEffect(() => {
    if (!isComponentMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!componentMenuRef.current) return;
      if (!componentMenuRef.current.contains(event.target as Node)) {
        closeComponentMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isComponentMenuOpen, closeComponentMenu]);

  const handleUploadFiles = async (files: File[]) => {
    if (attachedComponent) {
      const confirmed = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: 'Thay đổi đính kèm?',
          content: 'Bạn đang đính kèm thành phần. Thêm ảnh sẽ gỡ thành phần đã chọn. Tiếp tục?',
          okText: 'Tiếp tục',
          cancelText: 'Hủy',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) return;
      setAttachedComponent(null);
    }

    const validFiles: File[] = [];
    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        setToastMessage({ text: error, type: 'error' });
        return;
      }
      validFiles.push(file);
    }

    const currentCount = imageEntriesRef.current.length;
    const remainingSlots = MAX_IMAGES_COUNT - currentCount;
    if (remainingSlots <= 0) {
      setToastMessage({ text: `Bạn đã chọn đủ tối đa ${MAX_IMAGES_COUNT} ảnh`, type: 'error' });
      return;
    }

    const toAdd = validFiles.slice(0, remainingSlots);
    if (validFiles.length > remainingSlots) {
      setToastMessage({ text: `Bạn đã chọn đủ tối đa ${MAX_IMAGES_COUNT} ảnh`, type: 'error' });
    }

    const newEntries: ImageEntry[] = toAdd.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      previewUrl: URL.createObjectURL(file),
      status: 'uploading' as const,
      file,
    }));
    const entryIds = newEntries.map(e => e.id);

    setImageEntries(prev => [...prev, ...newEntries]);

    try {
      const token = sessionStorage.getItem('access_token');
      const conversationId = sessionStorage.getItem('current_conversation_id');
      if (!token || !conversationId) {
        setImageEntries(prev => prev.filter(e => !entryIds.includes(e.id)));
        newEntries.forEach(e => URL.revokeObjectURL(e.previewUrl));
        return;
      }

      const resizedFiles: File[] = [];
      for (const file of toAdd) {
        const resizedFile = await resizeImage(file);
        resizedFiles.push(resizedFile);
      }

      const uploadResponse = await chatService.uploadImages(
        token,
        parseInt(conversationId, 10),
        resizedFiles
      );

      const imgs = uploadResponse?.data;
      if (!imgs || imgs.length === 0) {
        setImageEntries(prev => prev.map(e =>
          entryIds.includes(e.id) ? { ...e, status: 'error' as const } : e
        ));
        return;
      }

      setImageEntries(prev => prev.map(e => {
        const idx = entryIds.indexOf(e.id);
        if (idx !== -1 && imgs[idx]) {
          return { ...e, status: 'uploaded' as const, uploadedData: imgs[idx] };
        }
        return e;
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
      setImageEntries(prev => prev.map(e =>
        entryIds.includes(e.id) ? { ...e, status: 'error' as const } : e
      ));
    }
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const files = Array.from(e.target.files);
    await handleUploadFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!e.clipboardData) return;

    const files = Array.from(e.clipboardData.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length > 0) {
      e.preventDefault();

      if (isThinking || uploadLoading || isChatInputDisabled) {
        return;
      }

      await handleUploadFiles(imageFiles);
    }
  };

  const handleRemoveImage = (index: number) => {
    setImageEntries((prev) => {
      const entry = prev[index];
      if (entry) URL.revokeObjectURL(entry.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleRetryUploadImage = async (index: number) => {
    const entry = imageEntriesRef.current[index];
    if (!entry) return;
    if (entry.status !== 'error') return;
    if (!entry.file) return;

    const token = sessionStorage.getItem('access_token');
    const conversationId = sessionStorage.getItem('current_conversation_id');
    if (!token || !conversationId) return;

    // Mark this single thumbnail as uploading to block double clicks.
    setImageEntries((prev) =>
      prev.map((e, i) =>
        i === index
          ? {
              ...e,
              status: 'uploading' as const,
            }
          : e,
      ),
    );

    try {
      const resizedFile = await resizeImage(entry.file);
      const uploadResponse = await chatService.uploadImages(
        token,
        parseInt(conversationId, 10),
        [resizedFile],
      );

      const imgs = uploadResponse?.data;
      if (!imgs || imgs.length === 0) {
        setImageEntries((prev) =>
          prev.map((e, i) =>
            i === index
              ? {
                  ...e,
                  status: 'error' as const,
                }
              : e,
          ),
        );
        return;
      }

      setImageEntries((prev) =>
        prev.map((e, i) =>
          i === index
            ? {
                ...e,
                status: 'uploaded' as const,
                uploadedData: imgs[0],
              }
            : e,
        ),
      );
    } catch (e) {
      console.error('Retry upload image failed:', e);
      setImageEntries((prev) =>
        prev.map((en, i) =>
          i === index
            ? {
                ...en,
                status: 'error' as const,
              }
            : en,
        ),
      );
    }
  };

  const handleInputChange = (value: string) => {
    setHideSuggestions(false);
    
    // Stop speaking if typing
    if (isBotSpeaking && onStopSpeaking) {
      onStopSpeaking();
    }
    
    onChangeInput(value);
  };

  const renderLeftIcon = () => {
    const suggestionDropdown = (commands.length > 0) ? (
        <QuickSelectionDropdown
            trigger={<SuggestionIcon />}
            items={commands}
            onSelect={onSelectCommand || (() => {})}
            isLoading={isCommandsLoading}
            type="COMMAND"
            showIndex={false}
            disabled={isThinking || uploadLoading}
            placement="left"
        />
    ) : null;

    switch (activeAgent) {
      case ChatAgentType.AI:
      case ChatAgentType.ANALYTICS_QUESTION:
        return (
            <div className="flex gap-2 items-center">
              {/* Temporarily hide component picker (+)
              {canShowComponentPicker && (
                <button
                  className={`input-message-action-btn input-message-plus-btn ${
                    isThinking || uploadLoading || !!quotedMessage ? 'disabled' : ''
                  }`}
                  type="button"
                  onClick={() => {
                    if (isComponentMenuOpen) {
                      closeComponentMenu();
                    } else {
                      setComponentDetailOpen(false);
                      setIsComponentMenuOpen(true);
                    }
                  }}
                  disabled={isThinking || uploadLoading || !!quotedMessage}
                  data-tooltip-id="chatbot-tooltip"
                  data-tooltip-content="Chọn thành phần"
                >
                  {isComponentMenuOpen ? (
                    <CloseIcon />
                  ) : (
                    <PlusOutlined style={{ fontSize: 16 }} />
                  )}
                </button>
              )}
              */}
              <button
                  className={`input-message-action-btn ${isThinking || uploadLoading || !!quotedMessage ? 'disabled' : ''}`}
                  type="button"
                  onClick={handleAttachClick}
                  disabled={isThinking || uploadLoading || !!quotedMessage}
                  data-tooltip-id="chatbot-tooltip"
                  data-tooltip-content="Thêm ảnh"
              >
                <AttachIcon />
              </button>
              {activeAgent === ChatAgentType.AI && (isQuickFunctionsLoading || quickFunctions.length > 0) && (
                  <QuickSelectionDropdown
                      trigger={<QuickCommandIcon />}
                      items={quickFunctions}
                      onSelect={onSelectQuickFunction || (() => {})}
                      isLoading={isQuickFunctionsLoading}
                      type="QUICK_FUNCTION"
                      disabled={isThinking || uploadLoading}
                      placement="left"
                  />
              )}
              {activeAgent === ChatAgentType.AI && (isInstructionsLoading || instructions.length > 0) && (
                  <QuickSelectionDropdown
                      trigger={<InstructionIcon />}
                      items={instructions.slice(0, 10)}
                      onSelect={onSelectInstruction || (() => {})}
                      isLoading={isInstructionsLoading}
                      type="INSTRUCTION"
                      disabled={isThinking || uploadLoading}
                      placement="left"
                  />
              )}
              {suggestionDropdown}
            </div>
        );
      case ChatAgentType.INSTRUCTION_MANUAL:
        return (
            <div className="flex gap-2 items-center">
              <button
                  className={`input-message-action-btn ${isThinking || uploadLoading || !!quotedMessage ? 'disabled' : ''}`}
                  type="button"
                  onClick={handleAttachClick}
                  disabled={isThinking || uploadLoading || !!quotedMessage}
                  data-tooltip-id="chatbot-tooltip"
                  data-tooltip-content="Thêm ảnh"
              >
                <AttachIcon />
              </button>
              {(isInstructionsLoading || instructions.length > 0) && (
                  <QuickSelectionDropdown
                      trigger={<InstructionIcon />}
                      items={instructions.slice(0, 10)}
                      onSelect={onSelectInstruction || (() => {})}
                      isLoading={isInstructionsLoading}
                      type="INSTRUCTION"
                      disabled={isThinking || uploadLoading}
                      placement="left"
                  />
              )}
              {suggestionDropdown}
            </div>
        );
      case ChatAgentType.CSKH:
        return (
            <div className="flex gap-2 items-center">
              <button
                  className={`input-message-action-btn ${isThinking || uploadLoading || !!quotedMessage ? 'disabled' : ''}`}
                  type="button"
                  onClick={handleAttachClick}
                  disabled={isThinking || uploadLoading || !!quotedMessage}
                  data-tooltip-id="chatbot-tooltip"
                  data-tooltip-content="Thêm ảnh"
              >
                <AttachIcon />
              </button>
              {suggestionDropdown}
            </div>
        );
      case ChatAgentType.TAX_QUESTION:
        return (
            <div className="flex gap-2 items-center">
              <button
                  className={`input-message-action-btn ${isThinking || uploadLoading || !!quotedMessage ? 'disabled' : ''}`}
                  type="button"
                  onClick={handleAttachClick}
                  disabled={isThinking || uploadLoading || !!quotedMessage}
                  data-tooltip-id="chatbot-tooltip"
                  data-tooltip-content="Thêm ảnh"
              >
                <AttachIcon />
              </button>
              {suggestionDropdown}
            </div>
        );
      default:
        return null;
    }
  };

  const onSend = () => {
    if (isThinking || isBotStreaming) return;

    const hasUnreadyImages = imageEntries.some(
      (e) => e.status === 'uploading' || e.status === 'error',
    );
    if (hasUnreadyImages) {
      setToastMessage({
        text: 'Ảnh chưa tải xong hoặc tải lỗi, bạn thử lại nhé',
        type: 'error',
      });
      return;
    }

    const hasText = inputValue.trim();
    const hasImages = imageEntries.some(e => e.status === 'uploaded' && e.uploadedData);
    const hasComponent = Boolean(attachedComponent);
    if (!hasText && !hasImages && !hasComponent) return;

    if (listening && stopMicAndDiscard) {
      stopMicAndDiscard();
    } else if (listening) {
      handleMicrophoneClick();
    }

    let uploadedImages: ChatUploadedImage[] | undefined;

    if (hasImages) {
      uploadedImages = imageEntries
        .filter(e => e.status === 'uploaded' && e.uploadedData)
        .map(e => e.uploadedData!);

      imageEntries.forEach(e => URL.revokeObjectURL(e.previewUrl));
      setImageEntries([]);
    }

    const componentToSend = attachedComponent;
    if (componentToSend) {
      setAttachedComponent(null);
    }

    if (hasText || (uploadedImages && uploadedImages.length > 0) || componentToSend) {
      onSendMessage(hasText ? inputValue : '', uploadedImages, componentToSend);
      if (hasText) {
        onChangeInput('');
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ignore Enter if the user is using an IME (Vietnamese Telex, Pinyin, etc.) and is currently composing a word
    if (event.nativeEvent.isComposing || event.keyCode === 229) {
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      if (!isThinking && !isBotStreaming) {
        onSend();
      }
    }
  };

  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputValue]);

  // Remove focus when listening starts, requirement says "không cho phép focus vào khung chat để nhập text"
  React.useEffect(() => {
    if (listening && textareaRef.current) {
        textareaRef.current.blur();
    }
  }, [listening]);

  const onMicClickInternal = () => {
    if (isThinking || isMicDisabled || uploadLoading) return;
    handleMicrophoneClick();
    // After clicking, if we are now listening, ensure focus is on the input
    // We use a small timeout to ensure the state update has propagated if needed, 
    // although the useEffect above should also handle it.
    setTimeout(() => {
      if (textareaRef.current && !listening) {
        textareaRef.current.focus();
      }
    }, 50);
  };

  const suggestionsData = getNumberSuggestions(inputValue);

  const showInstructionSuggestions =
    activeAgent === ChatAgentType.AI || activeAgent === ChatAgentType.INSTRUCTION_MANUAL;
  const showQuickFunctionSuggestions = activeAgent === ChatAgentType.AI;

  let { mode: keywordSuggestionMode } = parseKeywordSuggestionInput(inputValue);
  if (keywordSuggestionMode === 'QUICK_FUNCTION' && !showQuickFunctionSuggestions) {
    keywordSuggestionMode = null;
  }
  if (keywordSuggestionMode === 'INSTRUCTION' && !showInstructionSuggestions) {
    keywordSuggestionMode = null;
  }

  const quickFunctionSuggestions = showQuickFunctionSuggestions
    ? getQuickFunctionSuggestions(inputValue, quickFunctionSearchList)
    : [];
  const instructionSuggestions = showInstructionSuggestions
    ? getInstructionSuggestions(inputValue, instructions)
    : [];
  type KeywordSuggestionRow =
    | { kind: 'QUICK_FUNCTION'; item: any; displayLabel: string }
    | { kind: 'INSTRUCTION'; item: any; displayLabel: string };
  const keywordSuggestions: KeywordSuggestionRow[] =
    keywordSuggestionMode === 'QUICK_FUNCTION'
      ? quickFunctionSuggestions.map((item) => ({
          kind: 'QUICK_FUNCTION' as const,
          item,
          displayLabel: String(item.name || '').trim(),
        }))
      : keywordSuggestionMode === 'INSTRUCTION'
        ? instructionSuggestions.map((item) => ({
            kind: 'INSTRUCTION' as const,
            item,
            displayLabel:
              typeof item === 'string'
                ? item
                : String(
                    item.function_name ||
                      item.question ||
                      item.title ||
                      item.name ||
                      '',
                  ).trim(),
          }))
        : [];
  const showKeywordSuggestions =
    isFocused && !hideSuggestions && keywordSuggestions.length > 0 && keywordSuggestionMode !== null;

  const showNumberSuggestions =
    !showKeywordSuggestions && suggestionsData && activeAgent !== ChatAgentType.CSKH;

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const cursorPosition = event.target.selectionStart;
    onChangeInput(event.target.value, cursorPosition);
  };

  return (
    <div className="input-message-container relative">
      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileChange}
        multiple
        accept=".jpg,.jpeg,.png"
        style={{ display: 'none' }}
      />
      {showKeywordSuggestions && (
        <div
          className={`input-message-keyword-suggestions${
            keywordSuggestionMode === 'INSTRUCTION'
              ? ' input-message-keyword-suggestions--instruction'
              : ''
          }`}
          role="listbox"
        >          {keywordSuggestions.map((row, index) => (
            <button
              key={`${row.kind}-${index}`}
              type="button"
              role="option"
              className="input-message-keyword-suggestion-row"
              onMouseDown={(e) => {
                e.preventDefault();
                setHideSuggestions(true);
                onChangeInput(row.displayLabel);
                setTimeout(() => {
                  if (textareaRef.current) {
                    textareaRef.current.focus();
                    const valLen = row.displayLabel.length;
                    textareaRef.current.setSelectionRange(valLen, valLen);
                  }
                }, 10);
              }}
            >
            
            {keywordSuggestionMode === "INSTRUCTION" && 
            <LoadingIcon className="input-message-keyword-suggestion-icon" aria-hidden />}
            {keywordSuggestionMode === "QUICK_FUNCTION" && 
            <QuickCommandIcon className="input-message-keyword-suggestion-icon" aria-hidden />}
             
              <KeywordSuggestionLine mode={row.kind} text={row.displayLabel} inputValue={inputValue} />
            </button>
          ))}
        </div>
      )}

      {showNumberSuggestions && (
        <MoneySuggestions
          suggestions={suggestionsData.suggestions}
          onSelectSuggestion={(item) => {
            const before = inputValue.substring(0, suggestionsData.startIndex);
            const after = inputValue.substring(suggestionsData.endIndex);
            // Thêm khoảng trắng sau số để tránh trigger gợi ý tiếp cho số 0
            const newValue = `${before}${item.label} ${after}`;
            onChangeInput(newValue);

            // Auto focus back to input
            setTimeout(() => {
              if (textareaRef.current) {
                textareaRef.current.focus();
                // Move cursor to end
                const valLen = newValue.length;
                textareaRef.current.setSelectionRange(valLen, valLen);
              }
            }, 10);
          }}
        />
      )}

      <div className={`input-message-card ${listening ? 'is-listening' : ''}`}>
        {/* Temporarily hide component picker menu
        {canShowComponentPicker && isComponentMenuOpen && (
          <ComponentPicker
            allowedTypes={allowedTypes}
            selectedType={selectedType}
            onSelectType={selectType}
            detailOpen={componentDetailOpen}
            onOpenDetail={() => setComponentDetailOpen(true)}
            onBack={() => {
              setComponentDetailOpen(false);
              setSearchInput('');
            }}
            searchInput={searchInput}
            onSearchChange={setSearchInput}
            onSearchCommit={commitSearch}
            items={activeCache.items}
            loading={activeCache.loading}
            loadingMore={activeCache.loadingMore}
            error={activeCache.error}
            onLoadMore={loadMore}
            onPickItem={applyAttachedComponent}
            menuRef={componentMenuRef}
          />
        )}
        */}

        {/* Quoted Message Preview */}
        {quotedMessage && onCancelQuote && activeAgent && (
          <QuotedMessagePreview
            quotedMessage={quotedMessage}
            onCancel={onCancelQuote}
            activeAgent={activeAgent}
          />
        )}

        {attachedComponent && (
          <div className="mb-2 flex items-center gap-2 rounded-xl bg-[#f7f7f8] px-3 py-2">
            <span className="min-w-0 flex-1 truncate text-[14px] leading-5 text-[#242428]">
              {getComponentChipLabel(attachedComponent)}
            </span>
            <button
              type="button"
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#5c5c66] hover:bg-[rgba(61,61,67,0.08)]"
              onClick={() => setAttachedComponent(null)}
              aria-label="Gỡ thành phần"
            >
              <CloseIcon />
            </button>
          </div>
        )}

        {imageEntries.length > 0 && (
          <div className="image-preview-container">
            {imageEntries.map((entry, index) => (
              <div key={entry.id} className="image-preview-item">
                <img
                  src={entry.previewUrl}
                  alt={`Preview ${index + 1}`}
                  className="image-preview-thumbnail"
                />
                {entry.status === 'uploading' && (
                  <div className="image-preview-upload-overlay" aria-busy="true" aria-live="polite">
                    <LoadingOutlined className="image-preview-upload-spinner" />
                  </div>
                )}
                {entry.status === 'error' && (
                  <div
                    className="image-preview-upload-overlay image-preview-error-overlay"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleRetryUploadImage(index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleRetryUploadImage(index);
                    }}
                    aria-label="Thử lại gửi ảnh"
                  >
                    <WarningIcon className="image-preview-error-icon" />
                  </div>
                )}
                <button
                  type="button"
                  className="image-preview-remove"
                  disabled={entry.status === 'uploading'}
                  onClick={() => handleRemoveImage(index)}
                  aria-label="Remove image"
                >
                  <CloseIcon />
                </button>
              </div>
            ))}
          </div>
        )}

        <textarea
          ref={textareaRef}
          id="comment"
          className="input-message-textarea"
          placeholder={displayPlaceholder}
          value={inputValue}
          maxLength={2000}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={(e) => {
            if (listening) {
                e.target.blur();
                return;
            }
            setIsFocused(true);
          }}
          disabled={isChatInputDisabled}
          readOnly={listening}
          onBlur={() => {
            // Delay hide to allow click
            setTimeout(() => setIsFocused(false), 200);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          rows={1}
        />
        <div className="input-message-footer">
          <div className="input-message-left-actions">
            {renderLeftIcon()}
          </div>
          <div className="input-message-right-actions">
            {listening && (
                <div className="flex gap-2 items-center">
                    <button
                        className="input-message-action-btn stop-btn"
                        onClick={onMicClickInternal}
                        type="button"
                        data-tooltip-id="chatbot-tooltip"
                        data-tooltip-content={t('common.stopRecording') || 'Dừng'}
                    >
                       <MicroIconActive/>
                    </button>
                    {(inputValue.trim() || imageEntries.length > 0 || attachedComponent) && (
                        <button
                            onClick={onSend}
                            type="button"
                            className="input-message-action-btn send-btn active"
                        >
                            <SendIcon />
                        </button>
                    )}
                </div>
            )}
            {!listening && (
              isBotStreaming ? (
                <button
                  className="input-message-action-btn stop-stream-btn"
                  onClick={onStopStreaming}
                  type="button"
                  data-tooltip-id="chatbot-tooltip"
                  data-tooltip-content={t('common.stopStreaming') || 'Dừng tạo'}
                >
                  <PauseIcon />
                </button>
              ) : (
                (inputValue.trim() || imageEntries.length > 0 || attachedComponent) ? (
                  <button
                    onClick={onSend}
                    type="button"
                    className={`input-message-action-btn send-btn ${(isThinking || uploadLoading) ? 'disabled' : 'active'}`}
                    data-tooltip-id="chatbot-tooltip"
                    data-tooltip-content={t('common.send') || 'Gửi'}
                    disabled={isThinking || uploadLoading}
                  >
                    <SendIcon />
                  </button>
                ) : (
                  <button
                    className={`input-message-action-btn mic-btn ${(isMicDisabled || isThinking || uploadLoading) ? 'disabled' : ''}`}
                    onClick={onMicClickInternal}
                    onMouseUp={(e) => (e.target as HTMLButtonElement).blur()} // Prevent focus staying on button
                    type="button"
                    disabled={isMicDisabled || isThinking || uploadLoading}
                    data-tooltip-id="chatbot-tooltip"
                    data-tooltip-content={t('common.startRecording') || 'Thu âm'}
                  >
                    <MicroIcon />
                  </button>
                )
              )
            )}
          </div>
        </div>
      </div>
      <div className="input-message-disclaimer">
        {t('common.aiDisclaimer')}
      </div>
    </div>
  );
};

export default InputMessage;
